import { createPlatformActionParams } from '@/common/utils/requestSanitizer'
import { apiService } from '@/services/api.service'
import {
  coreService,
  createSafeClientData,
  FORM_PARSE_ATTEMPTED_FLAG,
  parsePlatformActionForm,
} from '@/services/core/core.service'
import type {
  ExecuteProcedureParams,
  FormData,
  FormPath
} from '@/services/core/types'
import { formHasRenderableLayout, normalizeFormOverlays, parseService } from '@/services/parse/parse.service'
import { defineStore } from 'pinia'
import { computed, nextTick, ref, toRaw } from 'vue'
import type { Router } from 'vue-router'
import {
  collectDescendantPageSlotIds,
  registryFormIdFromSlotId,
  resolvePageDataStorageKey,
  resolveStableParentPageSlotId,
} from '@/common/utils/registryFormIdFromSlotId'
import { getFormTitle } from '@/common/utils/formTitle'
import { useDocumentTabsStore } from '@/stores/documentTabs'
import { ActionError } from '@/common/exceptions'
import { setType } from '@/common/utils/typedValueSerializer'
import { buildDialogCloseConfigForStore } from '@/common/utils/dialogCloseParentMergeScope'
import { resolveParentSlotAtDialogOpen } from '@/common/utils/resolveDialogCloseParentStoreKey'
import { notifyPlatformSessionIdsChanged } from '@/services/form-events/platformSessionSync'
import { startWorkflowMeasure } from '@/common/utils/performanceTrace'
import {
  getPageIdentityAliases,
  resolveFormSchemaIdFromPageEntry,
} from '@/common/utils/resolvePageIdentity'
import { markFormPageBootstrap } from '@/common/utils/formPageBootstrap'
import { isolateJsResultForSlotMerge } from '@/common/utils/isolateJsResultSlotMerge'
import {
  capturePageSlotFieldSnapshot as capturePageSlotFieldSnapshotUtil,
  restorePageSlotFieldSnapshot as restorePageSlotFieldSnapshotUtil,
} from '@/common/utils/pageSlotFieldSnapshot'
import { normalizePlatformSessionId } from '@/common/utils/jsRequestSessionContext'
import { isPageDataCommitNoop } from '@/common/utils/pageDataCommitSignature'
import { finalizeDialogSlotCleanup, isDialogPageSlot } from '@/common/utils/dialogPageSlot'
import { clearDialogSlotRuntimeState } from '@/common/utils/formDialogRuntimeCleanup'
import {
  clearFormShellVisibilityRuntime,
  resolveFormSchemaChangeShellCleanup,
  resolvePageSlotShellCleanup,
} from '@/common/utils/formShellVisibilityCleanup'
import { boundDocumentIdentityChanged } from '@/common/utils/documentTransferIdentity'
import {
  isDistinctPlatformSessionChange,
  mergeFieldsDefensive,
  mergePageDataFieldsForSetPageData,
  pageDataTableRowCountsDiffer,
} from '@/common/utils/pageDataFieldMerge'
import {
  isMainFrameSlot,
  isMainFramePageData,
  isStartupMainFramePageData,
  MAIN_FRAME_SLOT_ID,
} from '@/common/utils/mainFrameSlot'
import {
  STARTUP_ACTION_DIALOG_FLAG,
  startupActionService,
} from '@/services/startup/startupAction.service'

interface Dialog {
  id: string
  sessionId: string
  config: any
  data?: any
  fid?: string | null
}

interface Overlay {
  id: string
  sessionId: string
  config: any
  name: string
  data?: any
  fid?: string | null
}

function getWorkflowDebugCaller(): string | undefined {
  try {
    const stack = new Error().stack
    if (!stack) return undefined
    const lines = stack.split('\n').map((line) => line.trim()).filter(Boolean)
    const caller = lines.find((line) =>
      !line.includes('getWorkflowDebugCaller') &&
      !line.includes('setPageData') &&
      !line.includes('app.ts'),
    )
    return caller?.replace(/^at\s+/, '').slice(0, 180)
  } catch {
    return undefined
  }
}

function normalizeIdentityLikeValue(value: unknown, depth = 0): string | null {
  if (value == null || depth > 3) return null
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed || trimmed.startsWith('[object')) return null
    if (trimmed.includes('typeId:') || trimmed.includes('document:') || trimmed.includes('view:')) {
      const match = trimmed.match(/(?:^|[\s,(;])(?:fid|id)\s*:\s*['"]?([^'",)\s]+)['"]?/i)
      return match?.[1]?.trim() || null
    }
    return trimmed
  }
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const o = value as Record<string, unknown>
    const candidates = [
      o.fid,
      o.id,
      o._id,
      o._text,
      o.value,
      o.formId,
      o.view,
      o.document,
    ]
    for (const candidate of candidates) {
      const normalized = normalizeIdentityLikeValue(candidate, depth + 1)
      if (normalized) return normalized
    }
  }
  return null
}

export function extractFormFid(document: any): string | null {
  if (!document) return null

  if (Array.isArray(document.stack) && document.stack.length > 0 && document.stack[0]) {
    return normalizeIdentityLikeValue(document.stack[0])
  }

  const formFid =
    document?.form?.form?._attributes?.fid ??
    document?.form?._attributes?.fid ??
    document?.form?.fid ??
    document?.$fid

  if (formFid) {
    return normalizeIdentityLikeValue(formFid)
  }

  const stateFid = document?.formState?.fid ?? document?.formState?._attributes?.fid
  return normalizeIdentityLikeValue(stateFid)
}

function pickRenderableFormFromPage(page: any): any {
  if (!page || typeof page !== 'object') return null
  const f = page.form
  if (f == null || typeof f !== 'object') return null
  if (formHasRenderableLayout(toRaw(f))) return f
  return null
}

/** Копирует поля uniData в payload action (только записи с type), без мутации исходной карты */
function copyTypedUniFieldsFromStore(target: Record<string, any>, source: Record<string, any> | undefined) {
  if (!source || typeof source !== 'object') return
  const raw = toRaw(source) as Record<string, any>
  for (const [key, value] of Object.entries(raw)) {
    if (value != null && typeof value === 'object' && 'type' in (value as object)) {
      target[key] = value
    }
  }
}

/** Как у UI (usePageData): поздний changed_data перекрывает uniData — часть полей только там */
function getEffectiveFieldMapFromPage(page: any): Record<string, any> {
  if (!page || typeof page !== 'object') return {}
  const uni = (page as any).uniData?.fields?.fields
  const ch = (page as any).jsResult?.changed_data?.fields?.fields
  const u = uni && typeof uni === 'object' && !Array.isArray(uni) ? (toRaw(uni) as Record<string, any>) : {}
  const c = ch && typeof ch === 'object' && !Array.isArray(ch) ? (toRaw(ch) as Record<string, any>) : {}
  return { ...u, ...c }
}

function effectiveFieldKeyCount(page: any): number {
  return Object.keys(getEffectiveFieldMapFromPage(page)).length
}

function docIdFromTypedFields(fields: Record<string, any>): string | null {
  const raw = fields?._id
  if (raw == null) return null
  if (typeof raw === 'object' && 'value' in raw && (raw as { value: unknown }).value != null) {
    return String((raw as { value: unknown }).value)
  }
  if (typeof raw === 'object') return null
  return String(raw)
}

function docIdFromEffectivePage(page: any): string | null {
  return docIdFromTypedFields(getEffectiveFieldMapFromPage(page))
}

function isTableLikeUniField(v: any): boolean {
  if (!v || typeof v !== 'object') return false
  const t = (v as any).type
  if (t === 'UniDataTable' || t === 'UniDataTableFields') return true
  return Array.isArray((v as any).value?.rows) || Array.isArray((v as any).rows)
}

function tableLikeFieldLooksEmpty(v: any): boolean {
  if (!v || typeof v !== 'object') return true
  const rows = (v as any).rows ?? (v as any).value?.rows
  return !Array.isArray(rows) || rows.length === 0
}

/** Донорские поля: новые ключи + подмена пустых таблиц, если в доноре есть строки */
function mergeDonorFieldsIntoDest(
  dest: Record<string, any>,
  donorEff: Record<string, any>,
): { added: number; replacedEmptyTables: number } {
  let added = 0
  let replacedEmptyTables = 0
  for (const [key, value] of Object.entries(donorEff)) {
    if (value == null || typeof value !== 'object') continue
    const rawDonor = toRaw(value) as any
    const copyVal =
      'type' in rawDonor && 'value' in rawDonor
        ? rawDonor
        : 'type' in rawDonor
          ? rawDonor
          : setType(value)

    const existing = dest[key]
    if (!(key in dest)) {
      dest[key] = copyVal
      added += 1
      continue
    }
    if (
      isTableLikeUniField(existing) &&
      isTableLikeUniField(copyVal) &&
      tableLikeFieldLooksEmpty(existing) &&
      !tableLikeFieldLooksEmpty(copyVal)
    ) {
      dest[key] = copyVal
      replacedEmptyTables += 1
    }
  }
  return { added, replacedEmptyTables }
}

/**
 * Если в меню нет IN_UNIDATA, getPage часто вызывается с fields={}.
 * Пустой currentData на POST /platform/action даёт ответ без контекста документа — «теряются» списки и значения.
 * Подставляем поля: слот цели → текущая страница → тот же sessionId → иначе слот с max ключами.
 * Источник — merge uniData.fields.fields + jsResult.changed_data.fields.fields (как на экране).
 */
/** Открытие корневого пункта меню (parentPageId === null): не тащим uniData/fid с другой вкладки. */
export function isIsolatedMenuRootGetPage(parentPageId: string | null | undefined): boolean {
  return parentPageId === null
}

export function augmentEmptyGetPageFields(
  typedFields: Record<string, any>,
  pageId: string,
  pagesDataRef: Record<string, any>,
  currentPageIdRef: string,
  options?: { isolateFromOtherPages?: boolean },
) {
  if (Object.keys(typedFields).length > 0) {
    return
  }

  const slot = pagesDataRef[pageId]
  const slotEff = getEffectiveFieldMapFromPage(slot)
  const slotN = Object.keys(slotEff).length
  if (slotN > 0) {
    copyTypedUniFieldsFromStore(typedFields, slotEff)
  }
  if (Object.keys(typedFields).length > 0) return

  if (options?.isolateFromOtherPages) {
    return
  }

  const curId = currentPageIdRef
  if (curId && String(curId) !== String(pageId)) {
    const cur = pagesDataRef[curId]
    const curEff = getEffectiveFieldMapFromPage(cur)
    const sidSlot = slot?.sessionId
    const sidCur = cur?.sessionId
    const curN = Object.keys(curEff).length
    const sessionMismatch =
      sidSlot != null && sidCur != null && String(sidSlot) !== String(sidCur)
    if (curN > 0 && !sessionMismatch) {
      copyTypedUniFieldsFromStore(typedFields, curEff)
    }
  }
  if (Object.keys(typedFields).length > 0) return

  // После doAction маршрут может смениться document→platform, а uniData лежит под другим ключом pagesData при том же sessionId
  const anchor = curId ? pagesDataRef[curId] : null
  const anchorSid = anchor?.sessionId != null ? String(anchor.sessionId) : null

  let best: Record<string, any> | undefined
  let bestN = 0
  if (anchorSid) {
    for (const p of Object.values(pagesDataRef)) {
      if (!p || typeof p !== 'object') continue
      if (p.sessionId == null || String(p.sessionId) !== anchorSid) continue
      const eff = getEffectiveFieldMapFromPage(p)
      const n = Object.keys(eff).length
      if (n > bestN) {
        bestN = n
        best = eff
      }
    }
    if (best && bestN > 0) {
      copyTypedUniFieldsFromStore(typedFields, best)
    }
  }

  // Документ часто в pagesData под id стека (напр. 3999), а маршрут — view id (2658): разный sessionId, пустой uni у «текущего» слота
  if (Object.keys(typedFields).length === 0) {
    let globalBest: Record<string, any> | undefined
    let globalN = 0
    for (const [k, p] of Object.entries(pagesDataRef)) {
      if (String(k) === String(pageId)) continue
      const eff = getEffectiveFieldMapFromPage(p)
      const n = Object.keys(eff).length
      if (n > globalN) {
        globalN = n
        globalBest = eff
      }
    }
    if (globalBest && globalN > 0) {
      copyTypedUniFieldsFromStore(typedFields, globalBest)
    }
  }
}

export type AppStore = ReturnType<typeof useAppStore>

export const useAppStore = defineStore('app', () => {
  // State
  const isLoading = ref<boolean>(false)
  const loadingText = ref<string>('Загрузка...')
  const sidebarCollapsed = ref<boolean>(false)
  const currentTheme = ref<'light' | 'dark'>('light')
  const notifications = ref<any[]>([])

  // Core state
  const loadPagesDataFromStorage = (): Record<string, any> => {
    // очищаем localStorage, потому что тут могут оказаться некорректные данные
    try { localStorage.removeItem('app_pagesData') } catch (_) { /* ignore */ }
    return {}
  }

  const pagesData = ref<Record<string, any>>(loadPagesDataFromStorage())
  const pagesDataRevision = ref(0)
  /** Последний слот setPageData — для side-effects только активной вкладки. */
  const lastPageDataCommitId = ref<string | null>(null)
  const dialogs = ref<Record<string, Dialog>>({})
  const dialogIds = ref<string[]>([])
  /** Session memory: slot ids ever opened as NEW_DIALOG (survives removeDialog). */
  const dialogPageSlotIds = ref<Set<string>>(new Set())
  /** Bumped on removeDialog — invalidates in-flight NEW_DIALOG open responses. */
  const dialogSlotEpoch = ref<Record<string, number>>({})

  const getDialogSlotEpoch = (id: string): number => {
    const key = String(id ?? '').trim()
    if (!key) return 0
    return dialogSlotEpoch.value[key] ?? 0
  }

  const bumpDialogSlotEpoch = (id: string): void => {
    const key = String(id ?? '').trim()
    if (!key) return
    dialogSlotEpoch.value = { ...dialogSlotEpoch.value, [key]: getDialogSlotEpoch(key) + 1 }
  }
  const overlays = ref<Record<string, Overlay>>({})
  const overlayIds = ref<string[]>([])
  const currentPageId = ref<string>('')
  const pendingPageNavigation = ref<{ id: string; parentId?: string | null } | null>(null)

  const bumpPagesDataRevision = () => {
    pagesDataRevision.value += 1
  }

  const persistPagesDataSessions = () => {
    try {
      localStorage.removeItem('app_pagesData')
    } catch (_) {
      /* ignore */
    }
  }

  const closeCoreForPageData = (
    pageData: any,
    options: { keepalive?: boolean; skipStartupAck?: boolean } = {},
  ) => {
    const sessionId = pageData?.sessionId ?? pageData?.$sessionId ?? null
    if (sessionId == null || String(sessionId).trim() === '' || String(sessionId) === '-1') {
      return
    }
    const skipStartupAck =
      options.skipStartupAck === true ||
      pageData?.config?.[STARTUP_ACTION_DIALOG_FLAG] === true ||
      pageData?.[STARTUP_ACTION_DIALOG_FLAG] === true
    void coreService.removeCore(String(sessionId), { ...options, skipStartupAck }).catch((error) => {
      console.warn('[AppStore] Failed to close core', { sessionId, error })
    })
  }

  const closeAllPlatformCoresOnUnload = () => {
    const startupSessionIds = new Set<string>()
    Object.values(dialogs.value).forEach((dialog: any) => {
      if (dialog?.config?.[STARTUP_ACTION_DIALOG_FLAG] === true && dialog?.sessionId) {
        startupSessionIds.add(String(dialog.sessionId))
      }
    })

    const closeEntry = (entry: any) => {
      const sessionId = entry?.sessionId ?? entry?.$sessionId ?? entry?.data?.sessionId ?? null
      const skipStartupAck =
        (sessionId != null && startupSessionIds.has(String(sessionId))) ||
        entry?.config?.[STARTUP_ACTION_DIALOG_FLAG] === true ||
        entry?.[STARTUP_ACTION_DIALOG_FLAG] === true
      closeCoreForPageData(entry, { keepalive: true, skipStartupAck })
    }

    Object.values(pagesData.value || {}).forEach(closeEntry)
    Object.values(dialogs.value || {}).forEach(closeEntry)
    Object.values(overlays.value || {}).forEach(closeEntry)
  }
  /** Last opened adjacent (split) page id per parent page id — so opening another doc in split replaces the shown one */
  const lastAdjacentPageIdByParent = ref<Record<string, string>>({})
  const currentPlatform = ref<string | null>(null)

  // Config state
  const configData = ref<Record<string, any>>({})
  const rawMenu = ref<any[]>([])
  const sideMenu = ref<any[]>([])
  const topMenu = ref<any[]>([])
  /** Chain data for Velocity templates in toolbar/menu labels */
  const sessionChainData = ref<any>(null)
  const imports = ref<Record<string, any>>({})
  const serverOnlyImportNames = ref<string[]>([])
  const lastActionParams = ref<any>(null)

  // Getters
  const unreadNotificationsCount = computed(() =>
    notifications.value.filter(n => !n.read).length
  )

  const topDialog = computed(() => {
    const ids = dialogIds.value
    return dialogs.value[ids[ids.length - 1]]
  })

  const topOverlay = computed(() => {
    const ids = overlayIds.value
    return overlays.value[ids[ids.length - 1]]
  })

  const currentSessionId = computed(() => {
    if (dialogIds.value.length) return topDialog.value?.sessionId
    else if (overlayIds.value.length) return topOverlay.value?.sessionId
    else return pagesData.value[currentPageId.value]?.sessionId
  })

  const sessionIdByFormId = computed(() => (formId: string) => {
    const wanted = String(formId ?? '').trim()
    if (!wanted) return '-1'

    const allForms = [
      ...Object.values(dialogs.value),
      ...Object.values(overlays.value),
      ...Object.values(pagesData.value),
    ]

    const direct = allForms.find((f) => f?.id != null && String(f.id) === wanted)
    if (direct?.sessionId != null) return direct.sessionId

    const byAlias = allForms.find((entry) => {
      if (!entry || typeof entry !== 'object') return false
      return getPageIdentityAliases(entry as Record<string, unknown>, {}).includes(wanted)
    })
    return byAlias?.sessionId ?? '-1'
  })

  const formStackEntries = computed(() => {
    const entries: Array<{ key: string; fid: string | null }> = []

    Object.values(pagesData.value || {}).forEach((page: any) => {
      const fid = extractFormFid(page)
      entries.push({
        key: `page:${page?.id ?? 'unknown'}`,
        fid
      })
    })

    overlayIds.value.forEach(id => {
      const overlay = overlays.value[id]
      const fid = overlay?.fid ?? extractFormFid(overlay?.data)
      entries.push({
        key: `overlay:${id}`,
        fid: fid ?? null
      })
    })

    dialogIds.value.forEach(id => {
      const dialog = dialogs.value[id]
      const fid = dialog?.fid ?? extractFormFid(dialog?.data)
      entries.push({
        key: `dialog:${id}`,
        fid: fid ?? null
      })
    })

    const seen = new Set<string>()
    return entries.filter(entry => {
      if (!entry.fid) return false
      if (seen.has(entry.fid)) {
        return false
      }
      seen.add(entry.fid)
      return true
    })
  })

  const formStackFids = computed(() =>
    formStackEntries.value.map(entry => entry.fid!) // filtered above
  )

  const currentFormFid = computed(() => {
    if (!formStackEntries.value.length) {
      return null
    }
    const last = formStackEntries.value[formStackEntries.value.length - 1]
    return last?.fid ?? null
  })

  // Actions
  const setLoading = (loading: boolean, text: string = 'Загрузка...') => {
    isLoading.value = loading
    loadingText.value = text
  }

  const toggleSidebar = () => {
    sidebarCollapsed.value = !sidebarCollapsed.value
  }

  const toggleTheme = () => {
    currentTheme.value = currentTheme.value === 'light' ? 'dark' : 'light'
  }

  const addNotification = (notification: any) => {
    notifications.value.unshift({
      ...notification,
      id: Date.now(),
      read: false,
      timestamp: new Date()
    })
  }

  const markNotificationAsRead = (id: number) => {
    const notification = notifications.value.find(n => n.id === id)
    if (notification) {
      notification.read = true
    }
  }

  // TODO:Обновление подписки тут?

  // Core Actions
  const setPageData = (
    id: string,
    data: any = {},
    parentPageId?: string | null,
    isAdjacentWindow?: boolean,
    debugSource?: string,
  ) => {
    const endMeasure = startWorkflowMeasure('appStore.setPageData', {
      id,
      sessionId: data?.sessionId,
      hasForm: Boolean(data?.form),
      source: debugSource ?? getWorkflowDebugCaller(),
    })
    if (data.currentChainData) {
      sessionChainData.value = data.currentChainData
    }
    const oldData = pagesData.value[id]

    data.id = id
    data.$cloneOf = data.cloneOf
    const normalizedSessionId = normalizePlatformSessionId(data.sessionId)
    if (normalizedSessionId) {
      data.sessionId = normalizedSessionId
    }
    data.$sessionId = data.sessionId
    data.$fid = extractFormFid(data)

    if(isAdjacentWindow !== undefined) {
      data.$isAdjacentWindow = isAdjacentWindow
    }

    if (dialogIds.value.some((dialogId) => String(dialogId) === String(id))) {
      data.$isDialog = true
    }

    if (data.$isDialog === true) {
      markDialogPageSlot(String(id))
    }

    // Track parent page for breadcrumb navigation
    if (parentPageId !== undefined && parentPageId !== null && parentPageId !== '') {
      data.$parentPageId = parentPageId
    } else if (parentPageId === null || parentPageId === '') {
      data.$parentPageId = undefined
    } else if (pendingPageNavigation.value?.id === id && pendingPageNavigation.value?.parentId !== undefined) {
      data.$parentPageId = pendingPageNavigation.value.parentId || undefined
      pendingPageNavigation.value = null
    } else if (oldData?.$parentPageId) {
      data.$parentPageId = oldData.$parentPageId
    } else if ((!oldData || !oldData.$parentPageId) &&
      currentPageId.value &&
      currentPageId.value !== '' &&
      currentPageId.value !== 'home' &&
      String(currentPageId.value) !== String(id)) {
      data.$parentPageId = currentPageId.value
    } else {
      // Clear parent if we're coming from home or no valid parent
      data.$parentPageId = undefined
    }

    if (data.$parentPageId) {
      const tabsStore = useDocumentTabsStore()
      const stableParent = resolveStableParentPageSlotId(
        data.$parentPageId,
        pagesData.value,
        tabsStore.tabIdsOrdered,
      )
      if (stableParent) {
        data.$parentPageId = stableParent
      }
    }

    if (isAdjacentWindow && data.$parentPageId) {
      const next = { ...lastAdjacentPageIdByParent.value }
      for (const [parentKey, pageId] of Object.entries(next)) {
        if (pageId === id && parentKey !== data.$parentPageId) {
          delete next[parentKey]
        }
      }
      next[data.$parentPageId] = id
      lastAdjacentPageIdByParent.value = next
    }

    const platform = data?.form?.form?._attributes?.platform || data?.form?.form?.platform?._text || data?.uniData?.clientData?.platform
    if (platform) {
      currentPlatform.value = platform
    }

    if (oldData && oldData?.sessionId !== data.sessionId && !data.cloneOf) {
      data.cloneOf = oldData.sessionId
    }

    if (data.form && !formHasRenderableLayout(data.form)) {
      const parseId = resolveFormSchemaIdFromPageEntry(data, id)
      const shouldParse =
        !data[FORM_PARSE_ATTEMPTED_FLAG] || typeof data.form === 'string'
      if (shouldParse) {
        const endParseMeasure = startWorkflowMeasure('parseService.getForm', {
          source: 'appStore.setPageData',
          formId: Number.isFinite(parseId) ? parseId : 0,
          isString: typeof data.form === 'string',
        })
        const parsed = parseService.getForm(
          data.form,
          Number.isFinite(parseId) ? parseId : 0,
        )
        endParseMeasure()
        data[FORM_PARSE_ATTEMPTED_FLAG] = true
        if (formHasRenderableLayout(parsed)) {
          data.form = parsed
        }
      }
    }

    if (data.form && typeof data.form === 'object') {
      const parseId = resolveFormSchemaIdFromPageEntry(data, id)
      normalizeFormOverlays(data.form, parseId)
    }

    if (oldData) {
      const sessionChanged = isDistinctPlatformSessionChange(
        oldData.sessionId,
        data.sessionId,
        { isAdjacentWindow },
      )
      const documentIdentityChanged = boundDocumentIdentityChanged(oldData, data)
      const replaceSessionScopedPayload = sessionChanged || documentIdentityChanged

      if ((!data.form || data.form === null || data.form === undefined) && oldData.form) {
        data.form = oldData.form
      } else if (
        oldData.form &&
        !formHasRenderableLayout(data.form) &&
        formHasRenderableLayout(oldData.form)
      ) {
        // Ответ без разметки (частичный JSON, пустой form и т.д.) — не затираем уже открытую вёрстку
        data.form = oldData.form
      } else if (
        data.form &&
        typeof data.form === 'object' &&
        oldData.form &&
        typeof oldData.form === 'object'
      ) {
        // re-parse мог обнулить <functions> (string → ._text === undefined) — вернуть с формы.
        const oldFns = (oldData.form as { functions?: unknown }).functions
        const newFns = (data.form as { functions?: unknown }).functions
        if (
          (typeof oldFns === 'string' && oldFns.trim()) &&
          !(typeof newFns === 'string' && newFns.trim())
        ) {
          ;(data.form as { functions?: unknown }).functions = oldFns
        }
      }

      if (oldData.uniData?.fields?.fields || replaceSessionScopedPayload) {
        const oldFields = oldData.uniData?.fields?.fields || {}
        const newFields = data.uniData?.fields?.fields || {}

        if (!data.uniData) {
          data.uniData = {}
        }
        if (!data.uniData.fields) {
          data.uniData.fields = {}
        }
        if (!data.uniData.fields.fields) {
          data.uniData.fields.fields = {}
        }

        data.uniData.fields.fields = mergePageDataFieldsForSetPageData(
          oldFields,
          newFields,
          replaceSessionScopedPayload,
        )
      }

      if (oldData.jsResult?.changed_data?.fields?.fields) {
        const oldChangedFields = oldData.jsResult.changed_data.fields.fields
        if (!data.jsResult) {
          data.jsResult = {}
        }
        if (!data.jsResult.changed_data) {
          data.jsResult.changed_data = {}
        }
        if (!data.jsResult.changed_data.fields) {
          data.jsResult.changed_data.fields = {}
        }
        if (!data.jsResult.changed_data.fields.fields) {
          data.jsResult.changed_data.fields.fields = {}
        }

        const newChangedFields = data.jsResult.changed_data.fields.fields || {}
        data.jsResult.changed_data.fields.fields = replaceSessionScopedPayload
          ? mergePageDataFieldsForSetPageData({}, newChangedFields, true)
          : mergeFieldsDefensive(oldChangedFields, newChangedFields)
      }

      const oldSchemaId = resolveFormSchemaIdFromPageEntry(
        oldData as Record<string, unknown>,
        id,
      )
      const newSchemaId = resolveFormSchemaIdFromPageEntry(
        data as Record<string, unknown>,
        id,
      )
      // SAME_WINDOW 506↔2581: drop stale Label shell before new onLoad states apply.
      const schemaShellCleanup = resolveFormSchemaChangeShellCleanup({
        slotId: String(id),
        oldSchemaId,
        newSchemaId,
      })
      if (schemaShellCleanup) {
        clearFormShellVisibilityRuntime(schemaShellCleanup)
      } else if (replaceSessionScopedPayload) {
        const sessionShellCleanup = resolvePageSlotShellCleanup({
          slotId: String(id),
          schemaId: newSchemaId || oldSchemaId,
        })
        if (sessionShellCleanup) {
          clearFormShellVisibilityRuntime(sessionShellCleanup)
        }
      }
      const preserveMissingStates =
        !replaceSessionScopedPayload &&
        oldData.jsResult?.states &&
        !data.jsResult?.states &&
        !(
          oldSchemaId > 0 &&
          newSchemaId > 0 &&
          String(oldSchemaId) !== String(newSchemaId)
        )

      if (preserveMissingStates) {
        if (!data.jsResult) {
          data.jsResult = {}
        }
        data.jsResult.states = oldData.jsResult.states
      }

      if (
        !replaceSessionScopedPayload &&
        oldData.jsResult?.event_listeners &&
        !data.jsResult?.event_listeners
      ) {
        if (!data.jsResult) {
          data.jsResult = {}
        }
        data.jsResult.event_listeners = oldData.jsResult.event_listeners
      }

      isolateJsResultForSlotMerge(String(id), oldData, data)
    }

    // Пункт меню и реальная форма — разные ключи pagesData; позже приходит частичный ответ без разметки.
    const tryBorrowFormFrom = (donor: any) => {
      const f = pickRenderableFormFromPage(donor)
      if (f == null) return false
      data.form = f
      const donorFields = donor?.uniData?.fields?.fields
      if (donorFields && typeof donorFields === 'object' && Object.keys(donorFields).length > 0) {
        if (!data.uniData) data.uniData = {}
        if (!data.uniData.fields) data.uniData.fields = {}
        const incoming = data.uniData.fields.fields || {}
        data.uniData.fields.fields = mergeFieldsDefensive(donorFields, incoming)
        if (!data.uniData.clientData && donor.uniData?.clientData) {
          data.uniData.clientData = { ...donor.uniData.clientData }
        }
      }
      return true
    }

    if (!formHasRenderableLayout(data.form)) {
      const sid = data.sessionId != null ? String(data.sessionId) : null
      if (sid != null) {
        for (const [otherId, page] of Object.entries(pagesData.value)) {
          if (String(otherId) === String(id)) continue
          const p = page as { sessionId?: unknown; form?: unknown }
          if (p?.sessionId == null) continue
          if (String(p.sessionId) !== sid) continue
          if (tryBorrowFormFrom(p)) break
        }
      }

      if (!formHasRenderableLayout(data.form)) {
        const incomingFid = extractFormFid(data)
        if (incomingFid) {
          for (const [otherId, page] of Object.entries(pagesData.value)) {
            if (String(otherId) === String(id)) continue
            if (extractFormFid(page) !== incomingFid) continue
            if (tryBorrowFormFrom(page)) break
          }
        }
      }

      // Частый случай: у «пустого» ответа другой sessionId, чем у открытой формы — не сравниваем sessionId.
      if (!formHasRenderableLayout(data.form)) {
        const exclude = String(id)
        const cur = currentPageId.value ? String(currentPageId.value) : ''
        const keys = Object.keys(pagesData.value).filter((k) => String(k) !== exclude)
        const ordered =
          cur && keys.includes(cur) ? [cur, ...keys.filter((k) => k !== cur)] : keys
        for (const otherId of ordered) {
          if (tryBorrowFormFrom(pagesData.value[otherId])) {
            break
          }
        }
      }
    }

    if (
      data.form != null &&
      typeof data.form === 'object' &&
      !formHasRenderableLayout(data.form)
    ) {
      delete data.form
    }

    if (
      isPageDataCommitNoop(String(id), oldData, data) &&
      !pageDataTableRowCountsDiffer(oldData, data)
    ) {
      endMeasure()
      return
    }

    pagesData.value = { ...pagesData.value, [id]: data }
    lastPageDataCommitId.value = id
    bumpPagesDataRevision()

    const dialogEntry = dialogs.value[id]
    if (dialogEntry) {
      const nextSessionId =
        data.sessionId != null ? String(data.sessionId) : dialogEntry.sessionId
      const schemaFid = resolveFormSchemaIdFromPageEntry(data as Record<string, unknown>, id)
      const nextFid =
        (schemaFid > 0 ? String(schemaFid) : null) ??
        extractFormFid(data) ??
        dialogEntry.fid
      const sessionChanged = dialogEntry.sessionId !== nextSessionId
      const fidChanged =
        nextFid != null &&
        String(nextFid).trim() !== '' &&
        String(dialogEntry.fid ?? '') !== String(nextFid)
      if (sessionChanged || fidChanged) {
        dialogs.value = {
          ...dialogs.value,
          [id]: {
            ...dialogEntry,
            sessionId: nextSessionId,
            ...(nextFid != null ? { fid: nextFid } : {}),
            data,
          },
        }
      }
    }

    // Persist sessionId to localStorage for page reload recovery
    persistPagesDataSessions()

    // Soft-events / onServerEvent: подписка на /client/event/form|{app}/{coreId} сразу
    const prevSid = normalizePlatformSessionId(oldData?.sessionId) ?? ''
    const nextSid = normalizePlatformSessionId(data.sessionId) ?? ''
    if (nextSid && prevSid !== nextSid && !data.$isAdjacentWindow) {
      notifyPlatformSessionIdsChanged()
    }

    endMeasure()

  }

  /** Remove a page from store. Stops pinging its core. */
  const removePage = (
    id: string,
    options?: { skipStartupReschedule?: boolean },
  ) => {
    if (!id) return
    const normalized = String(id)
    const removedPage = pagesData.value[normalized]
    const isStartupMainFrame = isMainFrameSlot(normalized) && isStartupMainFramePageData(removedPage)
    if (
      isDialogPageSlot(
        {
          dialogIds: dialogIds.value,
          dialogPageSlotIds: dialogPageSlotIds.value,
          pagesData: pagesData.value,
        },
        id,
      )
    ) {
      return
    }
    closeCoreForPageData(removedPage)
    const schemaId = resolveFormSchemaIdFromPageEntry(
      (removedPage ?? {}) as Record<string, unknown>,
      normalized,
    )
    const slotShellCleanup = resolvePageSlotShellCleanup({
      slotId: normalized,
      schemaId,
    })
    if (slotShellCleanup) {
      clearFormShellVisibilityRuntime(slotShellCleanup)
    }
    delete pagesData.value[id]
    pagesData.value = { ...pagesData.value }
    bumpPagesDataRevision()
    const next: Record<string, string> = {}
    Object.entries(lastAdjacentPageIdByParent.value).forEach(([parent, pageId]) => {
      if (pageId !== id) next[parent] = pageId
    })
    lastAdjacentPageIdByParent.value = next
    persistPagesDataSessions()

    if (isStartupMainFrame && !options?.skipStartupReschedule) {
      startupActionService.scheduleNextStartupActionAfterClose()
    }
  }

  const clearMainFrame = () => {
    if (!pagesData.value[MAIN_FRAME_SLOT_ID]) return
    removePage(MAIN_FRAME_SLOT_ID)
  }

  const snapshotMainFramePageData = (): Record<string, unknown> | null => {
    const snapshot = pagesData.value[MAIN_FRAME_SLOT_ID]
    if (!snapshot || !isMainFramePageData(snapshot)) {
      return null
    }
    return snapshot
  }

  /** Закрытие вкладки крестиком: слот + все потомки по $parentPageId. */
  const removePageAndDescendants = (id: string) => {
    const root = String(id ?? '').trim()
    if (!root) return
    const descendants = collectDescendantPageSlotIds(root, pagesData.value)
    for (const descId of descendants) {
      removePage(descId)
    }
    removePage(root)
  }

  /** Clear split for a parent so clicking that parent closes split */
  const clearAdjacentForParent = (parentPageId: string) => {
    if (!parentPageId) return
    const next = { ...lastAdjacentPageIdByParent.value }
    delete next[parentPageId]
    lastAdjacentPageIdByParent.value = next

    // Remove adjacent pages for this parent from store to close split
    const updatedPages = { ...pagesData.value }
    for (const [pageId, pageData] of Object.entries(updatedPages)) {
      if (pageData?.$isAdjacentWindow && pageData?.$parentPageId === parentPageId) {
        closeCoreForPageData(pageData)
        delete updatedPages[pageId]
      }
    }
    pagesData.value = updatedPages
    bumpPagesDataRevision()
    persistPagesDataSessions()
  }

  const markDialogPageSlot = (id: string) => {
    const normalized = String(id ?? '').trim()
    if (!normalized) return
    if (dialogPageSlotIds.value.has(normalized)) return
    dialogPageSlotIds.value = new Set(dialogPageSlotIds.value).add(normalized)
  }

  const addDialog = (data: Dialog) => {
    const fid = data.fid ?? extractFormFid(data.data)
    // Remove existing dialog with same ID first to ensure clean state
    const existingIndex = dialogIds.value.indexOf(data.id)
    if (existingIndex > -1) {
      dialogIds.value.splice(existingIndex, 1)
      delete dialogs.value[data.id]
    }
    // Add the new dialog
    dialogIds.value.push(data.id)
    dialogs.value = {
      ...dialogs.value,
      [data.id]: {
        ...data,
        fid: fid ?? null
      }
    }
    markDialogPageSlot(String(data.id))
  }

  const removeDialog = async (
    id: string,
    options?: { router?: Router; routePageId?: string },
  ) => {
    const normalized = String(id ?? '').trim()
    if (!normalized) return

    const hadDialog =
      dialogIds.value.includes(normalized) ||
      dialogs.value[normalized] != null ||
      pagesData.value[normalized] != null
    if (!hadDialog) return

    const dialogEntry = dialogs.value[normalized]
    const pageEntry = pagesData.value[normalized] as { $parentPageId?: string } | undefined
    const dialogConfig = dialogEntry?.config as Record<string, unknown> | undefined
    const isStartupActionDialog = dialogConfig?.[STARTUP_ACTION_DIALOG_FLAG] === true
    const parentSlotHint = String(
      dialogConfig?.pageDataSlot ??
        dialogConfig?.onCloseDialogParentSlotId ??
        pageEntry?.$parentPageId ??
        '',
    ).trim()
    const sessionId =
      dialogEntry?.sessionId ?? pagesData.value[normalized]?.sessionId ?? null
    if (sessionId) {
      await coreService.removeDialog(normalized, sessionId)
    }

    // Remove the specific dialog ID from the array, not just pop the last one
    const index = dialogIds.value.indexOf(normalized)
    if (index > -1) {
      dialogIds.value.splice(index, 1)
    }
    delete dialogs.value[normalized]
    dialogs.value = { ...dialogs.value }

    // Clean up pageData for this dialog to allow reopening with fresh data
    clearDialogSlotRuntimeState(
      normalized,
      pageEntry as Record<string, unknown> | undefined,
    )
    delete pagesData.value[normalized]
    pagesData.value = { ...pagesData.value }
    bumpPagesDataRevision()
    persistPagesDataSessions()

    await finalizeDialogSlotCleanup(
      {
        dialogIds: dialogIds.value,
        pagesData: pagesData.value,
        currentPageId: currentPageId.value,
        updatePageId,
      },
      normalized,
      {
        parentSlotHint,
        router: options?.router,
        routePageId: options?.routePageId,
      },
    )

    bumpDialogSlotEpoch(normalized)

    if (isStartupActionDialog) {
      startupActionService.scheduleNextStartupActionAfterClose()
    }

    // Don't clear lastActionParams here - it might be needed for reopening
    // lastActionParams.value = null
  }

  const removeAllDialogs = () => {
    Object.values(dialogs.value).forEach((dialog) => closeCoreForPageData(dialog))
    dialogIds.value = []
    dialogs.value = {}
    lastActionParams.value = null
  }

  const addOverlay = (data: Overlay) => {
    // Router push будет в компоненте
    const fid = data.fid ?? extractFormFid(data.data)
    overlayIds.value.push(data.id)
    overlays.value = {
      ...overlays.value,
      [data.id]: {
        ...data,
        fid: fid ?? null
      }
    }
  }

  const removeOverlay = (id: string) => {
    closeCoreForPageData(overlays.value[id])
    overlayIds.value.pop()
    delete overlays.value[id]
    overlays.value = { ...overlays.value }
    lastActionParams.value = null
  }

  const removeAllOverlays = () => {
    Object.values(overlays.value).forEach((overlay) => closeCoreForPageData(overlay))
    overlayIds.value = []
    overlays.value = {}
    lastActionParams.value = null
  }

  /** Сброс кеша открытых форм платформы (иначе getPage отдаёт старый слот без запроса к серверу). */
  const clearPlatformPagesCache = (
    options: { closeCores?: boolean; preserveMainFrame?: boolean } = {},
  ) => {
    const closeCores = options.closeCores !== false
    const preserveMainFrame = options.preserveMainFrame !== false
    const mainFrameSnapshot = preserveMainFrame ? snapshotMainFramePageData() : null
    if (
      Object.keys(pagesData.value).length === 0 &&
      Object.keys(lastAdjacentPageIdByParent.value).length === 0 &&
      pendingPageNavigation.value == null &&
      !mainFrameSnapshot
    ) {
      return
    }
    if (closeCores) {
      Object.entries(pagesData.value).forEach(([slotId, pageData]) => {
        if (slotId === MAIN_FRAME_SLOT_ID && mainFrameSnapshot) {
          return
        }
        closeCoreForPageData(pageData)
      })
    }
    pagesData.value = mainFrameSnapshot
      ? { [MAIN_FRAME_SLOT_ID]: mainFrameSnapshot }
      : {}
    bumpPagesDataRevision()
    lastAdjacentPageIdByParent.value = {}
    pendingPageNavigation.value = null
    try {
      localStorage.removeItem('app_pagesData')
    } catch (_) {
      /* ignore */
    }
    useDocumentTabsStore().resetWorkbenchTabs()
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('workflow:stale-platform-core', () => {
      clearPlatformPagesCache({ closeCores: false })
    })
  }

  const updatePageId = (id: string) => {
    currentPageId.value = id
    if (id === '' || id === 'home') {
      clearPlatformPagesCache()
    }
  }

  const setPendingPageNavigation = (id: string, parentId?: string | null) => {
    pendingPageNavigation.value = { id, parentId }
  }

  const listTabStoreBridge = (): AppStore =>
    ({
      pagesData: pagesData.value,
      setPageData,
    }) as AppStore

  const capturePageSlotFieldSnapshot = (slotId: string) => {
    capturePageSlotFieldSnapshotUtil(listTabStoreBridge(), slotId)
  }

  const restorePageSlotFieldSnapshot = (slotId: string): boolean => {
    return restorePageSlotFieldSnapshotUtil(listTabStoreBridge(), slotId)
  }

  const updateCurrentPlatform = (platform: string) => {
    currentPlatform.value = platform
  }

  const focusFormByFid = (
    fid: string,
    opts?: { router?: Router },
  ): { type: 'dialog' | 'overlay' | 'page'; id: string } | null => {
    if (!fid) {
      return null
    }

    const normalizedFid = String(fid)

    // Try dialogs first
    for (const id of dialogIds.value) {
      const dialog = dialogs.value[id]
      const dialogFid = dialog?.fid ?? extractFormFid(dialog?.data)
      if (dialogFid && String(dialogFid) === normalizedFid) {
        dialogIds.value = dialogIds.value.filter(existingId => existingId !== id).concat(id)
        dialogs.value = {
          ...dialogs.value,
          [id]: {
            ...dialog,
            fid: dialogFid
          }
        }
        return { type: 'dialog', id }
      }
    }

    // Overlays next
    for (const id of overlayIds.value) {
      const overlay = overlays.value[id]
      const overlayFid = overlay?.fid ?? extractFormFid(overlay?.data)
      if (overlayFid && String(overlayFid) === normalizedFid) {
        overlayIds.value = overlayIds.value.filter(existingId => existingId !== id).concat(id)
        overlays.value = {
          ...overlays.value,
          [id]: {
            ...overlay,
            fid: overlayFid
          }
        }
        return { type: 'overlay', id }
      }
    }

    // Finally pages
    const pageEntry = Object.values(pagesData.value || {}).find((page: any) => {
      const pageFid = page?.$fid ?? extractFormFid(page)
      return pageFid && String(pageFid) === normalizedFid
    })

    if (pageEntry?.id) {
      const pageId = pageEntry.id
      if (isDialogPageSlot({ dialogIds: dialogIds.value, dialogPageSlotIds: dialogPageSlotIds.value, pagesData: pagesData.value }, pageId)) {
        return null
      }
      updatePageId(pageId)
      const tabsStore = useDocumentTabsStore()
      const rawPage = pagesData.value[pageId] as Record<string, unknown> | undefined
      const existingTab = tabsStore.tabs.find((t) => t.id === pageId)
      const routeHint = existingTab?.routeName ?? 'document'
      tabsStore.openOrFocusDocumentTab(pageId, rawPage, routeHint)
      if (opts?.router) {
        const tabMeta = tabsStore.tabs.find((t) => t.id === pageId)
        const routeName =
          tabMeta?.routeName ?? (tabMeta?.type === 'list' ? 'platform' : 'document')
        void opts.router.push({ name: routeName, params: { id: pageId } })
      }
      return { type: 'page', id: pageId }
    }

    return null
  }

  // Platform Actions
  const getPage = async (payload: {
    fields: any
    id: string
    platform: string
    parentPageId?: string | null
    forceNetwork?: boolean
  }) => {
    const { fields, id, platform, parentPageId, forceNetwork } = payload
    const endMeasure = startWorkflowMeasure('appStore.getPage', {
      id,
      platform,
      parentPageId,
    })

    try {
      const cachedSlot = pagesData.value[id]
      if (!forceNetwork && cachedSlot?.sessionId != null && String(cachedSlot.sessionId) !== '') {
        const pr = pickRenderableFormFromPage(cachedSlot)
        const layoutOk = pr != null && formHasRenderableLayout(toRaw(pr))
        const eff = getEffectiveFieldMapFromPage(cachedSlot)
        let hasTableData = false
        for (const v of Object.values(eff)) {
          if (isTableLikeUniField(v) && !tableLikeFieldLooksEmpty(v)) {
            hasTableData = true
            break
          }
        }
        const uniRich = Object.keys(eff).length >= 12
        const slotIsKnownDialog =
          dialogIds.value.some((dialogId) => String(dialogId) === String(id)) ||
          dialogPageSlotIds.value.has(String(id))
        const canUseCacheWithoutLayout = !slotIsKnownDialog && (hasTableData || uniRich)
        if (layoutOk || canUseCacheWithoutLayout) {
          updatePageId(id)
          updateCurrentPlatform(platform)
          return cachedSlot
        }
      }

      const typedFields: Record<string, any> = {}
      if (fields && typeof fields === 'object') {
        const endPrepareFieldsMeasure = startWorkflowMeasure('appStore.getPage.prepareFields', {
          id,
          inputFieldCount: Object.keys(fields).length,
        })
        for (const [key, value] of Object.entries(fields)) {
          if (value && typeof value === 'object' && 'type' in value && 'value' in value) {
            typedFields[key] = value
          } else {
            typedFields[key] = setType(value)
          }
        }
        endPrepareFieldsMeasure()
      }

      const endAugmentFieldsMeasure = startWorkflowMeasure('appStore.getPage.augmentFields', {
        id,
        typedFieldCount: Object.keys(typedFields).length,
        currentPageId: currentPageId.value,
      })
      const isolateMenuRoot = isIsolatedMenuRootGetPage(parentPageId)
      augmentEmptyGetPageFields(typedFields, id, pagesData.value, currentPageId.value, {
        isolateFromOtherPages: isolateMenuRoot,
      })
      endAugmentFieldsMeasure()

      const slotAfterAugment = pagesData.value[id]
      let sessionIdForRequest: string | null =
        slotAfterAugment?.sessionId != null && String(slotAfterAugment.sessionId) !== ''
          ? String(slotAfterAugment.sessionId)
          : null
      let isCloneForRequest = sessionIdForRequest == null

      if (sessionIdForRequest == null) {
        const curPid = currentPageId.value
        if (curPid && String(curPid) !== String(id)) {
          const cur = pagesData.value[curPid]
          const curSid =
            cur?.sessionId != null && String(cur.sessionId) !== '' ? String(cur.sessionId) : ''
          if (curSid) {
            const incomingDocId = docIdFromTypedFields(typedFields)
            const curDocId = docIdFromEffectivePage(cur)
            if (incomingDocId && curDocId && incomingDocId === curDocId) {
              sessionIdForRequest = curSid
              isCloneForRequest = false
            }
          }
        }
      }

      const params = createPlatformActionParams({
        sessionId: sessionIdForRequest,
        isClone: isCloneForRequest,
        type: platform,
        actionType: 1,
        actionId: registryFormIdFromSlotId(String(id)),
        clientData: createSafeClientData({
          fields: typedFields
        }),
        openingParams: {
          ignoreExistingStack: isolateMenuRoot,
        },
      })

      const endGetWindowDataMeasure = startWorkflowMeasure('appStore.getPage.getWindowData', {
        id,
        actionId: params?.actionId,
        sessionId: params?.sessionId,
        fieldCount: Object.keys(typedFields).length,
      })
      const result = await getWindowData(params, parentPageId, {
        platform,
        pageSlotId: String(id),
      })
      endGetWindowDataMeasure()

      const endUpdatePageMeasure = startWorkflowMeasure('appStore.getPage.scheduleUpdatePage', {
        id,
        platform,
      })
      endUpdatePageMeasure()
      return result
    } finally {
      endMeasure()
    }
  }

  const getOverlay = async (context: any) => {
    setLoading(true, 'Загрузка оверлея...')
    try {
      const result = await coreService.getOverlay(context)
      if (!result.form) {
        console.error("EMPTY FORM")
        return result
      }

      addOverlay({
        id: context.params.actionId,
        sessionId: result.sessionId,
        config: context.config,
        name: getFormTitle(result) || 'overlay',
        data: result,
        fid: extractFormFid(result)
      })

      return result
    } catch (error) {
      console.error('Error getting overlay:', error)
      addNotification({
        type: 'error',
        message: 'Ошибка при загрузке оверлея',
        error
      })
      throw error
    } finally {
      setLoading(false)
    }
  }

  const getDialog = async (context: any) => {
    setLoading(true, 'Открытие диалога...')
    try {
      const result = await coreService.getDialog(context)
      if (!result.form) return

      const mergedCloseCfg = buildDialogCloseConfigForStore(
        result.clientDialogCloseConfig && typeof result.clientDialogCloseConfig === 'object'
          ? (result.clientDialogCloseConfig as Record<string, unknown>)
          : {},
        {
          ...(context.config && typeof context.config === 'object' ? context.config : {}),
        },
      )
      const parentSlotAtOpen = resolveParentSlotAtDialogOpen(
        { dialogIds: dialogIds.value, currentPageId: currentPageId.value },
        context.pageDataSlot ?? context.parentPageDataSlot,
        context.parentPageId,
      )
      if (parentSlotAtOpen) {
        mergedCloseCfg.pageDataSlot = parentSlotAtOpen
        mergedCloseCfg.onCloseDialogParentSlotId = parentSlotAtOpen
      }
      addDialog({
        id: context.params.actionId,
        sessionId: result.sessionId,
        config: Object.keys(mergedCloseCfg).length ? mergedCloseCfg : context.config,
        data: result,
        fid: extractFormFid(result)
      })

      return result
    } catch (error) {
      console.error('Error getting dialog:', error)
      addNotification({
        type: 'error',
        message: 'Ошибка при открытии диалога',
        error
      })
      throw error
    } finally {
      setLoading(false)
    }
  }

  const getWindowData = async (
    params: any,
    parentPageIdOverride?: string | null,
    options?: { deferCommit?: boolean; platform?: string; pageSlotId?: string },
  ) => {
    const storageSlotId = resolvePageDataStorageKey(options?.pageSlotId, params?.actionId)
    const endMeasure = startWorkflowMeasure('appStore.getWindowData', {
      actionId: params?.actionId,
      pageSlotId: storageSlotId,
      sessionId: params?.sessionId,
    })
    setLoading(true)
    try {
      const endCoreActionMeasure = startWorkflowMeasure('appStore.getWindowData.coreAction', {
        actionId: params?.actionId,
        sessionId: params?.sessionId,
      })
      const data = await coreService.action(params, { deferFormParse: true })
      endCoreActionMeasure()
      setLoading(false)
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
      })
      await nextTick()
      await parsePlatformActionForm(data, params?.actionId)

      const openedFormId = String(
        resolveFormSchemaIdFromPageEntry(data, params?.actionId) ?? params?.actionId ?? storageSlotId,
      )
      const { ensureWebClientOnLoadBeforeOpenForm } = await import(
        '@/services/action/webClientFormOnLoadBeforeBuild'
      )
      const prepared = await ensureWebClientOnLoadBeforeOpenForm(data as Record<string, unknown>, {
        appStore: {
          pagesData: pagesData.value,
          imports: imports.value,
        } as AppStore,
        openedFormId,
        parseForm: false,
      })
      Object.assign(data, prepared)

      // Always set pageData - forms are always requested from server (no caching)
      // If currentPageId is empty or 'home', don't set a parent (start fresh breadcrumb chain)
      let parentPageId = parentPageIdOverride
      if (parentPageId === undefined) {
        parentPageId = (currentPageId.value && currentPageId.value !== '' && currentPageId.value !== 'home')
          ? currentPageId.value
          : undefined
      }
      if (parentPageId && params?.actionId && String(parentPageId) === String(params.actionId)) {
        parentPageId = undefined
      }

      if (params?.clientData?.fields?.fields && Object.keys(params.clientData.fields.fields).length > 0) {
        const endMergeRequestFieldsMeasure = startWorkflowMeasure('appStore.getWindowData.mergeRequestFields', {
          actionId: params?.actionId,
          fieldCount: Object.keys(params.clientData.fields.fields).length,
        })
        if (!data.uniData) {
          data.uniData = {}
        }
        if (!data.uniData.fields) {
          data.uniData.fields = {}
        }
        if (!data.uniData.fields.fields) {
          data.uniData.fields.fields = {}
        }

        const typedInUnidataFields: Record<string, any> = {}
        for (const [key, value] of Object.entries(params.clientData.fields.fields)) {
          if (value && typeof value === 'object' && 'type' in value && 'value' in value) {
            typedInUnidataFields[key] = value
          } else {
            typedInUnidataFields[key] = setType(value)
          }
        }

        data.uniData.fields.fields = {
          ...data.uniData.fields.fields,
          ...typedInUnidataFields
        }

        if (data?.jsResult?.changed_data?.fields?.fields) {
          data.jsResult.changed_data.fields.fields = {
            ...data.jsResult.changed_data.fields.fields,
            ...typedInUnidataFields
          }
        }
        endMergeRequestFieldsMeasure()
      }

      // currentPageId ещё прежний (напр. 2658), updatePageId вызывается после getWindowData — подмешиваем недостающие / пустые таблицы с открытой формы
      if (Number(params?.actionType) === 1 && params?.actionId != null) {
        const endMergeDonorMeasure = startWorkflowMeasure('appStore.getWindowData.mergeDonorFields', {
          actionId: params?.actionId,
          currentPageId: currentPageId.value,
        })
        const targetId = String(params.actionId)
        const curId = currentPageId.value ? String(currentPageId.value) : ''
        if (curId && curId !== targetId && curId !== 'home') {
          const donorPage = pagesData.value[curId]
          const donorEff = getEffectiveFieldMapFromPage(donorPage)
          if (Object.keys(donorEff).length > 0) {
            if (!data.uniData) {
              data.uniData = {}
            }
            if (!data.uniData.fields) {
              data.uniData.fields = {}
            }
            if (!data.uniData.fields.fields || typeof data.uniData.fields.fields !== 'object') {
              data.uniData.fields.fields = {}
            }
            const dest = data.uniData.fields.fields as Record<string, any>
            mergeDonorFieldsIntoDest(dest, donorEff)
          }
        }
        endMergeDonorMeasure()
      }

      // Один документ в двух слотах (document vs пункт меню): не вешать в цепочку крошек оба id с одним fid
      if (parentPageId && params?.actionId != null) {
        const actionIdStr = String(params.actionId)
        const parentStr = String(parentPageId)
        if (parentStr !== actionIdStr) {
          const p = pagesData.value[parentStr]
          const incomingFid = extractFormFid(data)
          const parentFid = p?.$fid ?? extractFormFid(p)
          if (
            incomingFid &&
            parentFid &&
            String(incomingFid) === String(parentFid)
          ) {
            const grand = p?.$parentPageId
            parentPageId =
              grand && grand !== 'home' && String(grand) !== actionIdStr
                ? grand
                : undefined
          }
        }
      }

      const endSetPageDataMeasure = startWorkflowMeasure(
        options?.deferCommit
          ? 'appStore.getWindowData.scheduleSetPageData'
          : 'appStore.getWindowData.setPageData',
        {
        actionId: params?.actionId,
        parentPageId,
        sessionId: data?.sessionId,
        },
      )
      if (options?.deferCommit) {
        setTimeout(() => {
          const endCommitMeasure = startWorkflowMeasure('appStore.getWindowData.deferredSetPageData', {
            actionId: params?.actionId,
            parentPageId,
            sessionId: data?.sessionId,
          })
          try {
            setPageData(storageSlotId, data, parentPageId)
            updatePageId(storageSlotId)
            if (options.platform) {
              updateCurrentPlatform(options.platform)
            }
          } finally {
            endCommitMeasure()
          }
        }, 0)
      } else {
        setPageData(storageSlotId, data, parentPageId)
        markFormPageBootstrap(storageSlotId, 1200)
      }
      endSetPageDataMeasure()

      return data
    } catch (error) {
      if (!(error instanceof ActionError)) {
        console.error('Error getting window data:', error)
      }
      throw error
    } finally {
      setLoading(false)
      endMeasure()
    }
  }

  const executeProcedure = async (params: ExecuteProcedureParams) => {
    setLoading(true, 'Выполнение процедуры...')
    try {
      const result = await coreService.executeProcedure(params)
      return result
    } catch (error) {
      console.error('Error executing procedure:', error)
      addNotification({
        type: 'error',
        message: 'Ошибка при выполнении процедуры',
        error
      })
      throw error
    } finally {
      setLoading(false)
    }
  }

  const reloadElement = async (path: FormPath, form: FormData = {}) => {
    setLoading(true, 'Обновление элемента...')
    try {
      // Устанавливаем sessionId из store
      const enhancedPath = {
        ...path,
        sessionId: sessionIdByFormId.value(path.formId),
        type: form.platform || currentPlatform.value || form?.platformType || form?.platform || (pagesData.value[path.formId]?.form?.form?._attributes?.platform)
      }

      const endStoreReloadMeasure = startWorkflowMeasure('appStore.reloadElement.coreReload', {
        formId: path?.formId,
        elementId: path?.elementId,
        sessionId: enhancedPath.sessionId,
      })
      const result = await coreService.reloadElement(enhancedPath, form)
      endStoreReloadMeasure()
      return result
    } catch (error) {
      console.error('Error reloading element:', error)
      addNotification({
        type: 'error',
        message: 'Ошибка при обновлении элемента',
        error
      })
      throw error
    } finally {
      setLoading(false)
    }
  }

  // Config Actions
  const setConfig = (data: any) => {
    Object.assign(configData.value, data)
  }

  const setMenu = (menuData: { rawMenu: any[], sideMenu: any[], topMenu: any[] }) => {
    rawMenu.value = menuData.rawMenu
    sideMenu.value = menuData.sideMenu
    topMenu.value = menuData.topMenu
  }

  const clearMenu = () => {
    rawMenu.value = []
    sideMenu.value = []
    topMenu.value = []
  }

  const clearUserScopedConfig = () => {
    clearMenu()
    configData.value = {}
    imports.value = {}
    serverOnlyImportNames.value = []
    sessionChainData.value = null
    currentPlatform.value = null
    clearLastAction()
  }

  const setClientImports = (importsData: Record<string, any>) => {
    imports.value = importsData
  }

  const setServerOnlyImportNames = (names: string[]) => {
    serverOnlyImportNames.value = names
  }

  const setLastActionParams = (params: any) => {
    lastActionParams.value = params
  }

  const clearLastAction = () => {
    lastActionParams.value = null
  }

  /** Применить PlatformConfig (GET /platform/config или POST /user/change-user). */
  const applyPlatformConfig = (response: Record<string, any>) => {
    const data = { ...response }

    if (data.menu) {
      const parsedData = parseService.getMenu(data.menu)
      setMenu({
        rawMenu: parsedData.rawMenu,
        sideMenu: parsedData.sideMenu,
        topMenu: parsedData.topMenu,
      })
      delete data.menu
    }

    if (data.imports) {
      const parsedImports = parseService.parsePlatformImports(data.imports)
      setClientImports(parsedImports.clientImports)
      setServerOnlyImportNames(parsedImports.serverOnlyImportNames)
      delete data.imports
    }

    if (data.currentChainData) {
      sessionChainData.value = data.currentChainData
    }

    setConfig(data)
    return data
  }

  const getConfig = async () => {
    setLoading(true, 'Загрузка ...')
    try {
      const response = await apiService.platformConfig()
      return applyPlatformConfig(response)
    } catch (error) {
      console.error('Error loading config:', error)

      addNotification({
        type: 'error',
        message: 'Ошибка при загрузке конфигурации'
      })
      throw error
    } finally {
      setLoading(false)
    }
  }

  return {
    // State
    isLoading,
    loadingText,
    sidebarCollapsed,
    currentTheme,
    notifications,
    pagesData,
    pagesDataRevision,
    lastPageDataCommitId,
    dialogs,
    dialogIds,
    dialogPageSlotIds,
    dialogSlotEpoch,
    getDialogSlotEpoch,
    bumpDialogSlotEpoch,
    overlays,
    overlayIds,
    currentPageId,
    currentPlatform,
    pendingPageNavigation,
    lastAdjacentPageIdByParent,
    configData,
    rawMenu,
    sideMenu,
    topMenu,
    sessionChainData,
    imports,
    serverOnlyImportNames,
    lastActionParams,

    // Getters
    unreadNotificationsCount,
    topDialog,
    topOverlay,
    currentSessionId,
    sessionIdByFormId,
    formStackEntries,
    formStackFids,
    currentFormFid,

    // Actions
    setLoading,
    toggleSidebar,
    toggleTheme,
    addNotification,
    markNotificationAsRead,

    // Core actions
    setPageData,
    removePage,
    removePageAndDescendants,
    clearMainFrame,
    clearAdjacentForParent,
    addDialog,
    removeDialog,
    removeAllDialogs,
    addOverlay,
    removeOverlay,
    removeAllOverlays,
    clearPlatformPagesCache,
    closeCoreForPageData,
    closeAllPlatformCoresOnUnload,
    updatePageId,
    setPendingPageNavigation,
    capturePageSlotFieldSnapshot,
    restorePageSlotFieldSnapshot,
    updateCurrentPlatform,
    getPage,
    getOverlay,
    getDialog,
    getWindowData,
    executeProcedure,
    reloadElement,
    focusFormByFid,

    // Config actions
    setConfig,
    setMenu,
    clearUserScopedConfig,
    clearMenu,
    setClientImports,
    setServerOnlyImportNames,
    setLastActionParams,
    clearLastAction,
    applyPlatformConfig,
    getConfig
  }
})
